import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import urls from '../util/urls';
import {
  compose,
  withAccountHeader,
  withAuthHeader,
  withRetries,
  withConfiguredBaseUrl,
  withE2ERequestStubbing,
} from '../hooks/useService';
import { ServiceAuth } from '../hooks/useService/useService';

export default class SummaryService {
  summaryAPI: (auth: ServiceAuth) => AxiosInstance;

  constructor(_options?: Partial<AxiosRequestConfig>) {
    this.summaryAPI = (auth: ServiceAuth) =>
      compose(
        withE2ERequestStubbing,
        withAccountHeader(auth.account),
        withAuthHeader(auth.authToken),
        withConfiguredBaseUrl(auth.config),
        withRetries(),
      )(axios.create());
  }

  getAllProjectSummaries =
    (auth: ServiceAuth) => async (projectId: string, cohortId?: string) => {
      return this.summaryAPI(auth).get(
        urls.api.analytics.allSummaries(
          projectId,
          cohortId ? { cohortId } : {},
        ),
      );
    };

  getMedicationSummary =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      cohortId?: string;
      nextPageToken?: string;
      orderBy?: string;
    }) => {
      const { cohortId, projectId, ...rest } = params;
      return this.summaryAPI(auth).get(
        urls.api.analytics.medicationSummary(projectId, {
          ...rest,
          ...(cohortId ? { cohortId } : undefined),
        }),
      );
    };

  getProcedureSummary =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      cohortId?: string;
      nextPageToken?: string;
      orderBy?: string;
    }) => {
      const { cohortId, projectId, ...rest } = params;
      return this.summaryAPI(auth).get(
        urls.api.analytics.procedureSummary(projectId, {
          ...rest,
          ...(cohortId ? { cohortId } : undefined),
        }),
      );
    };

  getConditionSummary =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      cohortId?: string;
      nextPageToken?: string;
      orderBy?: string;
    }) => {
      const { cohortId, projectId, ...rest } = params;
      return this.summaryAPI(auth).get(
        urls.api.analytics.conditionSummary(projectId, {
          ...rest,
          ...(cohortId ? { cohortId } : undefined),
        }),
      );
    };

  getObservationSummary =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      cohortId?: string;
      nextPageToken?: string;
      orderBy?: string;
    }) => {
      const { cohortId, projectId, ...rest } = params;
      return this.summaryAPI(auth).get(
        urls.api.analytics.observationSummary(projectId, {
          ...rest,
          ...(cohortId ? { cohortId } : undefined),
        }),
      );
    };

  getMediaSummary =
    (auth: ServiceAuth) =>
    async (params: {
      projectId: string;
      cohortId?: string;
      nextPageToken?: string;
      orderBy?: string;
    }) => {
      const { cohortId, projectId, ...rest } = params;
      return this.summaryAPI(auth).get(
        urls.api.analytics.mediaSummary(projectId, {
          ...rest,
          ...(cohortId ? { cohortId } : undefined),
        }),
      );
    };
}
